import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { games, categories } from '@/mocks/games';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCat, setActiveCat] = useState(slug || 'role-playing');

  const currentCategory = categories.find((c) => c.slug === activeCat);
  const filteredGames = useMemo(
    () => games.filter((g) => g.category === activeCat),
    [activeCat]
  );

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-4">
            <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors no-underline">
                首页
              </Link>
              <span>/</span>
              <span className="text-foreground-800 font-medium">
                {currentCategory?.name || '游戏分类'}
              </span>
            </nav>
          </div>
        </div>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                {currentCategory?.name || '游戏分类'}
              </h1>
              <p className="mt-3 text-sm text-foreground-600 max-w-xl mx-auto">
                {currentCategory?.description || '按游戏类型浏览火影同人游戏'}
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveCat(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeCat === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700 border border-background-200/70'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={cat.icon}></i>
                    </div>
                    {cat.name}
                  </span>
                </button>
              ))}
            </div>

            {filteredGames.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
                  <i className="ri-gamepad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">暂无该分类的火影同人游戏</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                {filteredGames.map((game) => (
                  <div
                    key={game.id}
                    className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full h-44 md:h-48 overflow-hidden">
                      <img
                        src={game.image}
                        alt={game.title}
                        title={`${game.title} - ${currentCategory?.keyword || '火影同人游戏'}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>

                    <div className="p-4">
                      <h3 className="font-heading font-bold text-foreground-950 text-sm md:text-base mb-2">
                        {game.title}
                      </h3>
                      <p className="text-xs text-foreground-600 leading-relaxed mb-3 line-clamp-2">
                        {game.description}
                      </p>

                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {game.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 bg-accent-100 text-accent-800 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <div className="flex items-center gap-1">
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-star-fill text-accent-500"></i>
                          </div>
                          <span className="font-semibold text-foreground-800">{game.rating}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-gamepad-line"></i>
                          </div>
                          <span>{game.plays} 次游玩</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory?.name || '游戏分类'} - 火影同人游戏合集`,
            description: currentCategory?.description || '火影同人游戏分类浏览',
            url: `https://naruto-fangame.com/category/${activeCat}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '火影同人游戏合集',
              url: 'https://naruto-fangame.com/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://naruto-fangame.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: currentCategory?.name || '游戏分类',
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}